import type { BlogPost } from './blogTypes';
import { commonLinks, ctaAgents, ctaDemo, ctaTrial } from './blogCommonLinksAndCtas';

type ComparisonGuideFields = Pick<
  BlogPost,
  'intro' | 'excerpt' | 'readTime' | 'seo' | 'keyTakeaways' | 'sections' | 'faq'
>;

export const shipdayAlternativeGuideExpanded: ComparisonGuideFields = {
  excerpt:
    'Shipday-style tools excel at getting small teams from order to route quickly. This guide explains when that simplicity stops being an advantage—multi-hub networks, mixed fleets and carriers, SLA-driven customers, and analytics-heavy operations—and how to evaluate a next-generation platform without boiling the ocean.',
  readTime: '22 min read',
  intro:
    'If you are reading this, you probably do not have a “routing problem” in the narrow sense. You have a coordination problem: more nodes in the network, more exceptions per thousand stops, and more stakeholders who need a single operational truth. Lightweight delivery software can be the right starting point; the question is what breaks first when volume and contractual expectations climb.',
  seo: {
    documentTitle: 'Shipday Alternative: When to Upgrade | Geofleet',
    metaDescription:
      'Outgrowing Shipday-style delivery tools? Learn the signals (multi-hub, carriers, SLAs), evaluation criteria, and migration tips for an execution platform.',
  },
  keyTakeaways: [
    {
      lead: 'The pain is rarely “bad maps.”',
      text: 'It is fragmented planning, dispatch, customer communication, and finance reconciliation that no longer fits in one lightweight product surface.',
    },
    {
      lead: 'Hub count changes the math.',
      text: 'Inventory placement, cutoffs, and cross-dock handoffs introduce constraints that local tour builders were never asked to represent.',
    },
    {
      lead: 'Carriers are a product feature.',
      text: 'Once you blend owned fleet with contracted capacity, you need allocation rules, performance history, and exception ownership—not only a polyline.',
    },
    {
      lead: 'Evaluate with exception weeks, not happy paths.',
      text: 'Pilot scenarios should include surges, address errors, refused access, partial picks, and refund-triggering failures.',
    },
    {
      lead: 'Migration is change management.',
      text: 'Parallel-run one region, freeze scope, measure manual minutes removed, and only then expand automation.',
    },
  ],
  sections: [
    {
      type: 'p',
      text: 'Shipday and similar products occupy a valuable niche: they help small and mid-sized delivery teams turn a list of stops into a workable plan with minimal setup. For many operators, that is exactly the right trade early on—fast onboarding, predictable pricing, and a narrow surface area so dispatchers can train new hires in days rather than weeks. The friction appears gradually. First you add a second warehouse, then a few dedicated routes become pooled capacity, then a retailer asks for branded tracking links and penalty clauses tied to time windows. Each change is manageable in isolation; together they turn your weekly operations meeting into a sequence of workarounds.',
    },
    {
      type: 'p',
      text: 'This article is not a feature checklist intended to declare a “winner.” It is a practical framework for deciding whether your next system should still optimize tours, or whether it must optimize a network: dispatch policies, allocation, live execution, customer messaging, and analytics that finance trusts. You will see how to separate cosmetic gaps from structural ones, how to run a pilot that surfaces real latency and failure modes, and how to keep your team productive during a transition.',
    },
    {
      type: 'h2',
      text: 'What lightweight delivery software optimizes for',
    },
    {
      type: 'p',
      text: 'Tools in this category typically prioritize speed-to-value. They assume orders arrive in reasonable shape, drivers belong to a small known group, and exceptions are rare enough to handle manually. That assumption holds for many restaurant, grocery, and local courier use cases where dispatchers can hold most of the day in their heads. The product experience rewards simplicity: build a route, send it to phones, track completion, and reconcile at the end of the shift. When those assumptions hold, adding heavyweight enterprise features would only slow teams down.',
    },
    {
      type: 'p',
      text: 'The challenge is that growth rarely preserves those assumptions. A second brand enters your network and demands isolated reporting. A retail partner requires proof-of-delivery photos with structured metadata. Finance asks for cost per stop broken down by depot and by carrier contract tier. At that point, the original tool still “works” in the sense that routes can be drawn, but the organization begins paying a hidden tax: exports, spreadsheets, and side channels that reintroduce the complexity you thought you had avoided.',
    },
    {
      type: 'h2',
      text: 'Signals that you are outgrowing a simple delivery stack',
    },
    {
      type: 'ul',
      items: [
        'You operate two or more hubs that share inventory or drivers, and planners spend meaningful time reconciling which depot owns which stop after the fact.',
        'You run a mix of employee drivers, owner-operators, and third-party carriers, and assignment decisions require business rules rather than intuition.',
        'Customer-facing teams escalate weekly because tracking links, ETAs, or proof artifacts do not match what support sees internally.',
        'Peak weeks force manual “war rooms” because automated replanning cannot respect contractual constraints across regions.',
        'Finance and operations disagree on cost per delivery because mileage, overtime, and carrier invoices are stitched together outside the system of record.',
      ],
    },
    {
      type: 'p',
      text: 'None of these signals implies the incumbent product failed. They usually mean your operating model crossed a threshold where tour optimization is necessary but not sufficient. The question becomes whether your platform can represent the objects you now manage—hubs, contracts, SLAs, inventory buffers, and exception workflows—or whether those objects live in email and spreadsheets.',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'A simple threshold test',
      text: 'If your dispatchers build the daily plan in a spreadsheet before they touch the routing tool, you have already outgrown tour-only software. The spreadsheet is encoding business rules the product cannot represent—and that hidden layer is where errors, rework, and finance disputes are born.',
    },
    {
      type: 'h2',
      text: 'Lightweight tools vs an execution platform: a side-by-side',
    },
    {
      type: 'p',
      text: 'The table below is qualitative on purpose. The point is not that one column is universally better; it is to show where the categories diverge so you can map each row to your own pain. A single-van weekend operation should be comfortable on the left; a multi-hub, SLA-bound network usually needs the right.',
    },
    {
      type: 'table',
      caption: 'Lightweight delivery tools vs an execution platform like Geofleet (qualitative)',
      headers: ['Capability', 'Lightweight delivery tool', 'Execution platform (Geofleet)'],
      rows: [
        ['Setup and time-to-value', 'Days; minimal configuration', 'Guided onboarding; models hubs, carriers, and SLAs'],
        ['Core unit of work', 'A route or tour', 'A network: orders, inventory, vehicles, contracts'],
        ['Dispatch automation', 'Manual or basic auto-assign', 'AI-assisted assignment with rules and approvals'],
        ['Exception handling', 'Handled manually, often in chat', 'First-class workflows with reattempt and ownership'],
        ['ETA quality', 'Distance/heuristic estimates', 'Traffic- and telemetry-aware, shared with CX'],
        ['Proof of delivery', 'Photo/signature capture', 'Structured proofs tied to the dispatch and finance record'],
        ['Multi-hub & carriers', 'Limited or workaround-based', 'Native allocation rules and carrier scorecards'],
        ['Analytics', 'Activity counts (stops, miles)', 'Decision metrics: cost per success, exception dwell'],
        ['Pricing model', 'Low, flat, predictable', 'Scales with depth; weigh against manual-labor savings'],
      ],
    },
    {
      type: 'h2',
      text: 'Multi-hub logistics is a different problem than multi-stop routing',
    },
    {
      type: 'p',
      text: 'Adding a depot changes more than distance matrices. It changes cutoffs, pick waves, cross-dock eligibility, and sometimes which customer promises are even achievable. A planner might need to hold capacity at one hub while another hub absorbs overflow from a promotion. Static route templates cannot express those dynamics; they optimize geometry without modeling the business rules that determine which inventory can serve which demand.',
    },
    {
      type: 'p',
      text: 'If your team already builds daily plans in spreadsheets before they touch routing, that is a strong hint you need network-aware dispatch. The goal is not fancier maps; it is a consistent model where orders, inventory positions, and vehicle states align before anyone commits routes to drivers.',
    },
    {
      type: 'h2',
      text: 'Carrier mix and performance management',
    },
    {
      type: 'p',
      text: 'Blending owned fleet with external carriers introduces questions lightweight tools rarely answer natively: Which lanes should never be outsourced? How do you weight on-time history versus cost? What happens when a carrier rejects a manifest at the eleventh hour? These are allocation and governance problems. They require historical performance signals, structured escalation paths, and sometimes financial controls such as caps or surge pricing rules.',
    },
    {
      type: 'p',
      text: 'Teams often try to solve this with naming conventions and color codes in a dispatch board. That works until volume grows and handoffs multiply. A sustainable approach treats carriers as first-class participants with measurable outcomes, not as rows on a whiteboard.',
    },
    {
      type: 'h2',
      text: 'Integrations: from CSV to event-driven operations',
    },
    {
      type: 'p',
      text: 'Early-stage stacks tolerate nightly CSV drops because latency is measured in hours. As order intake accelerates—ecommerce injects, marketplace orders, or POS-driven replenishment—batch schedules become liabilities. Integrations should be evaluated on failure modes as much as features: partial files, duplicate keys, idempotent writes, and replay after outages. Ask vendors how their APIs behave when upstream systems flap during peak.',
    },
    {
      type: 'p',
      text: 'Also examine whether integrations are symmetrical. Ingesting orders is only half the story; you must push status transitions, proof artifacts, and adjustments back to commerce and ERP systems so refunds, invoices, and inventory adjustments stay aligned.',
    },
    {
      type: 'h2',
      text: 'Customer experience and SLA reality',
    },
    {
      type: 'p',
      text: 'Branded tracking pages are not vanity. They reduce “where is my order” contacts, set expectations before failures become complaints, and provide an auditable communication trail when disputes arise. If your SLA program lives in a separate portal from where dispatchers work, you will see drift: customers see one ETA while the internal plan shows another. The operational fix is unified event streams and clear ownership of customer-facing promises.',
    },
    {
      type: 'p',
      text: 'When evaluating alternatives, insist on worst-case scenarios: address corrections mid-route, customer not home, restricted access, and partial deliveries. Pretty happy-path demos rarely reveal whether CX tooling breaks under pressure.',
    },
    {
      type: 'h2',
      text: 'Analytics: from activity metrics to decision metrics',
    },
    {
      type: 'p',
      text: 'Activity metrics count stops and miles. Decision metrics tie operations to revenue and cost: cost per successful delivery, first-attempt success, exception dwell time, and carrier penalty exposure. Mature teams build weekly reviews around a small set of decision metrics that both dispatch and finance recognize as authoritative. If your current stack cannot produce those metrics without manual joins, you will remain reactive.',
    },
    {
      type: 'p',
      text: 'Look for cohort views: by depot, by channel, by carrier, and by customer segment. Growth introduces heterogeneity; averages hide the pockets where service deteriorates first.',
    },
    {
      type: 'h2',
      text: 'How to run a fair evaluation',
    },
    {
      type: 'ol',
      items: [
        'Define three scenarios: a normal Tuesday, a promotional surge, and a disruption week with hub congestion or carrier shortfall.',
        'Bring real order files with sensitive fields redacted rather than vendor sample data.',
        'Measure time-to-first-good-plan and time-to-recovery after mid-day injections or cancellations.',
        'Trace a single order across OMS, dispatch, driver app, customer notifications, and finance reconciliation.',
        'Score governance: roles, approvals, audit logs, and data retention posture for your industry.',
      ],
    },
    {
      type: 'p',
      text: 'Keep scoring rubrics explicit. Teams that rely on gut feel during demos often select the most polished interface rather than the system that survives production entropy.',
    },
    {
      type: 'callout',
      variant: 'stat',
      title: 'Where the day actually goes',
      value: '70%+',
      text: 'In many growing operations, the share of a dispatcher’s day spent on exceptions and reconciliation—not initial planning—climbs well past two-thirds as volume and hub count rise. That is the cost a lightweight tool quietly externalizes onto your team.',
    },
    {
      type: 'h2',
      text: 'Migration without stalling the business',
    },
    {
      type: 'p',
      text: 'Parallel operation beats big-bang cutovers for most networks. Pick one city or one depot, freeze scope for thirty to sixty days, and measure operational outcomes against a baseline week. Train dispatchers on exception workflows first; perfect routing on day one matters less than predictable recovery when things go wrong. Document integrations as contracts: field mappings, retry policies, and escalation owners.',
    },
    {
      type: 'p',
      text: 'Communicate to customers only after internal confidence is high. Nothing erodes trust faster than a new tracking experience that contradicts dispatch reality.',
    },
    {
      type: 'h2',
      text: 'Where Geofleet fits in this journey',
    },
    {
      type: 'p',
      text: 'Geofleet is built for execution-grade logistics: AI-assisted dispatch, routing under disruption, real-time visibility, and automation without losing human control. It is aimed at teams that have graduated from drawing routes to running a network with accountability across hubs, carriers, and customers. If your near-term problem is still occasional weekend deliveries with a single van, a lighter tool may remain appropriate. If your week is defined by cross-functional exception handling, it is worth seeing whether a command-center model reduces manual coordination materially.',
    },
    {
      type: 'p',
      text: 'Use comparison pages alongside this guide for side-by-side context, then validate with a pilot that reflects your messiest operational realities—not your cleanest spreadsheet.',
    },
    {
      type: 'ctaRow',
      items: [ctaAgents, ctaDemo],
    },
    {
      type: 'relatedReading',
      heading: 'Compare and explore',
      links: [
        commonLinks.shipday,
        commonLinks.lastMile,
        commonLinks.routeOpt,
        commonLinks.onfleet,
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaTrial, ctaAgents],
    },
  ],
  faq: [
    {
      question: 'Does evaluating an alternative mean we have to leave Shipday immediately?',
      answer:
        'No. Many teams run parallel pilots in one region while the rest of the network stays on the incumbent. The goal is evidence: measure manual minutes, exception handling time, and customer contact rates under comparable volume before committing to a full migration.',
    },
    {
      question: 'What is the first metric that usually breaks when teams outgrow lightweight tools?',
      answer:
        'Exception dwell time—the minutes between an incident (failed access, wrong address, partial pick) and a resolved plan that finance and CX can defend. When dispatchers and leads spend hours in side chats, the tool is no longer the constraint; coordination overhead is.',
    },
    {
      question: 'How important are native integrations versus APIs?',
      answer:
        'Both matter. APIs provide flexibility but shift integration burden to your team. Native connectors reduce time-to-value for common stacks. Ask how webhooks and batch recovery work in practice, not only whether a logo appears on a slide.',
    },
    {
      question: 'Should we prioritize routing quality or dispatch automation first?',
      answer:
        'Prioritize whichever caps your growth today. If plans are decent but humans cannot keep up with changes, automation and governance matter more. If plans are structurally wrong because hubs and inventory are not modeled, routing quality must come first.',
    },
    {
      question: 'What role should finance play in vendor selection?',
      answer:
        'Finance should validate that cost allocation, carrier invoices, and refund logic can be traced to operational events. Otherwise you will ship excellent service while margins drift.',
    },
    {
      question: 'How long should a pilot last?',
      answer:
        'Long enough to include at least one peak or promotional event and one disruption. Four to eight weeks is typical for mid-sized networks, shorter only if volume is low and exceptions are rare.',
    },
    {
      question: 'What is a common migration mistake?',
      answer:
        'Copying every legacy workflow verbatim. Some steps exist only because the old system could not represent a rule. Migration is a chance to simplify—if you standardize exception codes and ownership first.',
    },
    {
      question: 'Is Geofleet only for large enterprises?',
      answer:
        'Geofleet targets teams that need execution depth—multi-hub coordination, carrier mix, and automation—rather than a specific employee count. A single-region operator with heavy SLA requirements may still benefit from a command-center approach.',
    },
  ],
};

export const routificAlternativeGuideExpanded: ComparisonGuideFields = {
  excerpt:
    'Routific and similar route planners shine at sequencing stops for small crews. This long-form guide explains when sequencing is no longer the bottleneck—multi-depot networks, exception storms, carrier handoffs, and customer-facing execution—and how to evaluate platforms that combine routing with dispatch, visibility, and automation.',
  readTime: '22 min read',
  intro:
    'Most “Routific alternative” searches are really asking whether your next system should still behave like a tour builder, or like an operating system for delivery. The difference is not map polish; it is who owns live state when plans change, how exceptions propagate, and whether your integrations can keep finance and customers aligned.',
  seo: {
    documentTitle: 'Routific Alternative: Routing vs Execution | Geofleet',
    metaDescription:
      'Looking for a Routific alternative? See when route planning is not enough, how to evaluate execution platforms, multi-depot pitfalls, and migration tactics.',
  },
  keyTakeaways: [
    {
      lead: 'Sequencing is not dispatch.',
      text: 'Ordering stops brilliantly on a calm morning does not solve reassignment, partial failures, or proof-of-delivery governance when the day goes sideways.',
    },
    {
      lead: 'Spreadsheets are a symptom.',
      text: 'When planners export routes to edit capacity, carrier rules, or customer tiers, the tool has stopped matching the business model.',
    },
    {
      lead: 'Multi-depot breaks single-board mental models.',
      text: 'Depots introduce inventory, cutoffs, and cross-dock rules that require network state, not only geometry.',
    },
    {
      lead: 'Integrations should be bidirectional.',
      text: 'Ingesting orders is insufficient if statuses, proofs, and adjustments cannot flow back to commerce and ERP systems reliably.',
    },
    {
      lead: 'Pilot for exceptions.',
      text: 'Choose scenarios with injections, cancellations, and access failures—those determine whether a platform survives production.',
    },
  ],
  sections: [
    {
      type: 'p',
      text: 'Routific earned adoption by helping teams convert messy stop lists into sensible tours with minimal ceremony. For many small and mid-sized fleets, that value proposition remains correct: fast planning, approachable UX, and a tight loop between dispatcher and driver. Problems emerge when the organization needs the plan to survive contact with reality—mid-day order spikes, drivers calling in sick, customers changing windows, or finance questioning why miles and overtime do not match the invoice.',
    },
    {
      type: 'p',
      text: 'This guide separates situations where a route planner remains the right category from situations where you need logistics execution: live dispatch boards, structured exceptions, carrier governance, and analytics that connect operations to margin. You will see how to diagnose your real bottleneck, how to evaluate vendors without being distracted by feature matrices, and how to migrate without freezing the business.',
    },
    {
      type: 'h2',
      text: 'What Routific-style tools do exceptionally well',
    },
    {
      type: 'p',
      text: 'Tour builders focus on converting a set of constraints—time windows, capacities, skills—into an ordered sequence. When constraints are stable and the team is small enough to coordinate informally, that focus is a strength. Dispatchers can iterate quickly, compare scenarios, and push routes to mobile workflows without navigating enterprise bureaucracy. Training cost stays low, which matters when turnover is non-trivial.',
    },
    {
      type: 'p',
      text: 'The design trade is intentional: fewer knobs means faster outcomes for straightforward operations. Difficulty appears when those knobs become necessary: contract tiers with different service levels, multiple depots with overlapping territories, or integrations that must preserve inventory truth—not only addresses.',
    },
    {
      type: 'h2',
      text: 'When route optimization stops being the bottleneck',
    },
    {
      type: 'ul',
      items: [
        'Dispatchers spend more time reconciling spreadsheets than editing routes inside the tool.',
        'Customer promises live in a different system than driver reality, creating distrust in tracking pages.',
        'Exceptions generate a parallel workflow in chat apps because the product cannot represent refusal codes or reattempt policies.',
        'Adding a depot duplicates planning effort instead of centralizing constraints.',
        'Carrier onboarding requires bespoke instructions every time because rules are not encoded as data.',
      ],
    },
    {
      type: 'p',
      text: 'If several bullets resonate, you are likely constrained by execution infrastructure rather than polyline quality. Another incremental routing tweak will not reduce support tickets when the underlying issue is fragmented state.',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Diagnose the bottleneck before you shop',
      text: 'Ask one question in your next operations review: when a plan goes wrong, how many minutes pass before a new plan exists that finance and CX can both defend? If the answer is "hours" or "a chat thread," your constraint is execution, not sequencing—and no route planner will fix it.',
    },
    {
      type: 'h2',
      text: 'Route planner vs execution platform: how the categories differ',
    },
    {
      type: 'p',
      text: 'A tour builder and an execution platform are optimizing different things. The first optimizes a sequence; the second optimizes the live state of a network as that sequence collides with reality. Use the comparison below to decide which problem dominates your week.',
    },
    {
      type: 'table',
      caption: 'Route planner vs delivery execution platform (qualitative)',
      headers: ['Dimension', 'Route planner (Routific-style)', 'Execution platform (Geofleet)'],
      rows: [
        ['Primary job', 'Sequence stops well', 'Run the network when the plan changes'],
        ['Live reassignment', 'Re-solve, then re-share manually', 'In-flight reassignment with proofs preserved'],
        ['Exceptions', 'Edited by hand, tracked in chat', 'Coded refusal/reattempt workflows with ownership'],
        ['Customer ETAs', 'Often static vs driver reality', 'Live ETAs from the same operational graph'],
        ['Carrier rules', 'Encoded informally', 'Allocation policies as data, with history'],
        ['Integrations', 'CSV / one-way ingest', 'Bidirectional events to commerce and ERP'],
        ['Best fit', 'Single-dispatcher, stable constraints', 'Multi-depot, SLA-driven, mixed fleet'],
      ],
    },
    {
      type: 'h2',
      text: 'From single-board dispatch to network dispatch',
    },
    {
      type: 'p',
      text: 'Single-board mental models work when everyone sees the same picture and exceptions are rare. Network dispatch requires knowing which hub owns inventory, which carrier tier is eligible for a lane, and which SLA applies to a customer segment. Those questions are not solved by sorting stops faster; they require persistent objects and permissions so planners cannot accidentally violate a contract while trying to help.',
    },
    {
      type: 'p',
      text: 'Signs of network complexity include cross-dock transfers, staged loads, and pooled drivers who can float between territories. If your planners maintain those rules verbally, you are carrying enterprise complexity in a tool built for speed.',
    },
    {
      type: 'h2',
      text: 'Exceptions are the hidden workload',
    },
    {
      type: 'p',
      text: 'Perfect routes rarely survive the day. Address errors, access restrictions, partial picks, and customer cancellations are routine at scale. The cost is not only missed miles; it is coordination time—who owns the ticket, what the customer sees, and how refunds or redelivery charges flow. Systems that treat exceptions as first-class workflows reduce variance and protect margin.',
    },
    {
      type: 'p',
      text: 'When evaluating alternatives, ask for exception throughput: how many concurrent incidents, how reassignment preserves proofs, and how audit logs read during disputes. Averages hide p95 pain.',
    },
    {
      type: 'callout',
      variant: 'stat',
      title: 'Exceptions, not miles, drive cost variance',
      value: '5-10x',
      text: 'A single failed delivery can cost several times more than a successful one once you add redelivery miles, support time, refunds, and SLA penalties. Teams that treat exceptions as first-class workflows—rather than chat threads—report markedly lower cost variance at the same volume.',
    },
    {
      type: 'h2',
      text: 'Customer visibility and proof artifacts',
    },
    {
      type: 'p',
      text: 'Modern buyers expect accurate ETAs and trustworthy proof of delivery. That requires telematics-aware timing, not only straight-line estimates, and structured capture for photos, signatures, and notes. If proofs live outside the dispatch record, chargebacks and service credits become contentious.',
    },
    {
      type: 'p',
      text: 'Branded tracking is part of the product you sell to shippers. Treat it as operational infrastructure, not marketing chrome.',
    },
    {
      type: 'h2',
      text: 'Integration depth: events, not only files',
    },
    {
      type: 'p',
      text: 'Batch CSV imports can bootstrap a pilot. Sustained operations need idempotent APIs, webhook retries, and clear conflict resolution when two systems disagree about order state. Evaluate vendors with failure injection: delayed files, duplicate events, and partial outages. Resilient integrations cost engineering time up front and save executive time later.',
    },
    {
      type: 'p',
      text: 'Bidirectional sync matters because delivery outcomes trigger financial events. A delivered scan should propagate cleanly; a failed attempt should not require manual ERP edits at midnight.',
    },
    {
      type: 'h2',
      text: 'Building a scorecard that reflects your network',
    },
    {
      type: 'ul',
      items: [
        'Time-to-stable-plan: minutes from cutoffs to committed routes under normal load.',
        'Recovery time: minutes to a new feasible plan after a mid-day disruption.',
        'Exception dwell: end-to-end minutes to clear a failed attempt with customer communication.',
        'Cost fidelity: variance between modeled cost and actual carrier or payroll outcomes.',
        'CX alignment: percentage of customer-visible ETAs matching internal operational truth.',
      ],
    },
    {
      type: 'p',
      text: 'Weight the scorecard toward metrics your CEO and COO already track. Beautiful maps do not pay invoices.',
    },
    {
      type: 'h2',
      text: 'Pilot design that surfaces reality',
    },
    {
      type: 'p',
      text: 'Run pilots on messy weeks. Include promotional volume, a carrier shortfall, and a day with abnormal traffic. Parallel operate in one depot or region so you can compare support ticket volume and manual interventions apples-to-apples. Capture baseline metrics from the week before so leadership sees delta, not anecdotes.',
    },
    {
      type: 'p',
      text: 'Document explicit go/no-go criteria before the pilot starts to avoid moving goalposts when teams get excited about a new UI.',
    },
    {
      type: 'h2',
      text: 'Change management and operator trust',
    },
    {
      type: 'p',
      text: 'Dispatchers tolerate experimentation when they see fewer midnight surprises, not when automation hides state. Roll out guardrails: approvals for sensitive moves, shadow mode for AI suggestions, and transparent logs when the system overrides a human choice. Trust compounds when operators can explain decisions to drivers and customers.',
    },
    {
      type: 'p',
      text: 'Training should emphasize exception flows first. Anyone can follow a green day; professionals earn their keep on red days.',
    },
    {
      type: 'h2',
      text: 'Where Geofleet fits relative to route planning tools',
    },
    {
      type: 'p',
      text: 'Geofleet targets teams that need routing plus execution: AI-assisted dispatch, real-time adjustments, monitoring, and automation with governance. It is not trying to win a spreadsheet shootout on the smallest possible micro-market; it is trying to reduce operational variance at network scale. If your organization still fits comfortably in a single-dispatcher mental model, a focused tour builder may remain economical. If your leadership meetings are dominated by exceptions, integration gaps, and SLA risk, a broader platform may reduce total cost of ownership even if licensing is higher on paper.',
    },
    {
      type: 'p',
      text: 'Pair this article with the Routific comparison page for positioning, then validate with scenarios sampled from your own peak weeks.',
    },
    {
      type: 'ctaRow',
      items: [ctaAgents, ctaDemo],
    },
    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        commonLinks.routific,
        commonLinks.routeOpt,
        commonLinks.lastMile,
        commonLinks.shipday,
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaTrial, ctaAgents],
    },
  ],
  faq: [
    {
      question: 'Should we replace Routific entirely or augment it?',
      answer:
        'Augmentation can work temporarily if routing stays strong but execution tooling is missing elsewhere. Long term, split systems of record for dispatch state tend to diverge. Most mature teams converge on one execution platform to avoid double entry and conflicting ETAs.',
    },
    {
      question: 'What is the biggest misconception in “alternative” evaluations?',
      answer:
        'Equating more features with better outcomes. Teams win by reducing exception time and integration fragility—even if the map looks similar day one.',
    },
    {
      question: 'How do we compare total cost fairly?',
      answer:
        'Include licensing, integration build, manual reconciliation hours, and customer support load. A cheaper subscription that requires nightly spreadsheet surgery is often more expensive.',
    },
    {
      question: 'What about driver apps?',
      answer:
        'Evaluate offline behavior, proof capture, and clarity when routes change mid-shift. Drivers adopt tools that reduce ambiguity, not those that add taps without reducing failures.',
    },
    {
      question: 'Do we need AI on day one?',
      answer:
        'Not necessarily. You need reliable state, integrations, and exception workflows. AI amplifies good operations; it cannot substitute for missing data contracts.',
    },
    {
      question: 'How long does migration take?',
      answer:
        'Depends on integration complexity and hub count. Practical pilots often run four to twelve weeks; full network cutovers may span quarters when ERP touchpoints are heavy.',
    },
    {
      question: 'What should legal or compliance review?',
      answer:
        'Data processing agreements, retention for proofs, and auditability of automated decisions if regulators or partners ask for traceability.',
    },
    {
      question: 'Where should I start reading inside Geofleet?',
      answer:
        'Review AI agents and command-center capabilities alongside routing to see how suggestions, approvals, and monitoring connect—not only how stops are sequenced.',
    },
  ],
};

export const locusLogisticsAlternativesExpanded: ComparisonGuideFields = {
  excerpt:
    'Locus and similar AI routing engines help enterprises optimize tours with rich constraints. This guide explains when advanced routing must be paired with execution, dispatch governance, carrier orchestration, and customer visibility—and how to evaluate alternatives without duplicating planning in spreadsheets.',
  readTime: '22 min read',
  intro:
    'Enterprise routing platforms can generate impressive plans. The gap appears at the moment those plans meet drivers, hubs, carriers, and customers. If your organization spends more energy reconciling plans with reality than debating objective weights, you are ready to think beyond pure optimization outputs toward operational systems that close the loop.',
  seo: {
    documentTitle: 'Locus Alternatives: AI Routing to Execution | Geofleet',
    metaDescription:
      'Evaluating Locus alternatives? Learn when AI routing is not enough, which execution features matter, how to pilot across hubs, and where Geofleet fits in.',
  },
  keyTakeaways: [
    {
      lead: 'Optimization outputs are not operational truth.',
      text: 'Until statuses, proofs, and financial events align, even excellent routes remain hypotheses.',
    },
    {
      lead: 'Constraints live in multiple systems.',
      text: 'Inventory, carrier contracts, and customer SLAs must converge before solvers run—otherwise math optimizes the wrong problem.',
    },
    {
      lead: 'Hybrid stacks are normal.',
      text: 'Some teams keep a specialist routing engine and add an execution layer; others consolidate. Choose based on integration latency and ownership clarity.',
    },
    {
      lead: 'Measure solver time and recovery time.',
      text: 'Academic optimality matters less than how quickly planners can regenerate feasible plans after disruptions.',
    },
    {
      lead: 'Automation needs guardrails.',
      text: 'AI dispatch shines with approvals, audit logs, and role boundaries—especially across partners.',
    },
  ],
  sections: [
    {
      type: 'p',
      text: 'Locus represents a class of sophisticated routing and field-service optimization products aimed at enterprises with complex constraints: skills, time windows, vehicle classes, and cost layers. Teams adopt these tools to squeeze mileage, balance workload, and respect business rules that simpler tour builders cannot express. The value is real. The friction emerges when optimized plans must be executed by humans and machines that live outside the solver—carriers who reject loads, hubs that run out of pick capacity, customers who change delivery windows after optimization completes.',
    },
    {
      type: 'p',
      text: 'This article frames “Locus alternatives” broadly. Sometimes the right move is a different solver; often it is an execution platform that consumes solver output—or replaces a patchwork—so dispatchers, drivers, and customers share one timeline. You will learn how to diagnose whether your pain is modeling, latency, integration, or governance, and how to structure pilots that respect enterprise procurement reality.',
    },
    {
      type: 'h2',
      text: 'What enterprise AI routing does well',
    },
    {
      type: 'p',
      text: 'Advanced solvers handle heterogeneity: multiple objectives, soft constraints, and large problem sizes that break naive heuristics. For organizations with mature data and disciplined master data management, they can materially reduce distance and overtime while exposing trade-offs planners can explain to leadership. That capability matters when incremental points of margin are worth millions at fleet scale.',
    },
    {
      type: 'p',
      text: 'The prerequisite is clean inputs. Solvers amplify data quality problems: duplicate stops, wrong geocodes, and inventory mismatches become “optimal” plans that fail on the ground. Teams sometimes underestimate the data engineering tax when they buy optimization software.',
    },
    {
      type: 'h2',
      text: 'Where routing-only stacks struggle',
    },
    {
      type: 'ul',
      items: [
        'Execution systems do not receive plan updates quickly enough when real-time events invalidate assumptions.',
        'Carrier allocation happens outside the solver via email, so optimized routes are manually reworked anyway.',
        'Customer communication is decoupled, so ETAs shown publicly diverge from the latest internal solve.',
        'Proof-of-delivery and financial posting require manual bridges when statuses cannot flow bidirectionally.',
        'Multiple hubs lack a unified dispatch model; each region optimizes locally while the network suboptimizes globally.',
      ],
    },
    {
      type: 'p',
      text: 'None of these items negates solver value. They indicate that optimization is necessary but not sufficient for the outcomes executives measure: margin, SLA adherence, and customer trust.',
    },
    {
      type: 'callout',
      variant: 'stat',
      title: 'Garbage in, optimal garbage out',
      value: '~80%',
      text: 'In most enterprise deployments, the majority of "the optimizer is wrong" complaints trace back to input data—stale inventory, bad geocodes, duplicate stops—rather than the solver itself. Budget for the data-engineering tax before you budget for more optimization horsepower.',
    },
    {
      type: 'h2',
      text: 'Routing engine vs execution platform: where each wins',
    },
    {
      type: 'p',
      text: 'Comparing a pure optimization engine to an execution platform is not apples-to-apples; they often coexist. The matrix below clarifies which layer owns which job so you can decide between hybrid and consolidation deliberately, rather than by accident of procurement.',
    },
    {
      type: 'table',
      caption: 'AI routing engine vs execution platform (qualitative; many enterprises run both)',
      headers: ['Concern', 'AI routing engine (Locus-style)', 'Execution platform (Geofleet)'],
      rows: [
        ['Core strength', 'Deep constraint modeling and solve quality', 'Live dispatch, visibility, and exception recovery'],
        ['Owns dispatch state', 'Up to the moment of the solve', 'Through execution and post-delivery events'],
        ['Reacts to disruption', 'Requires a re-solve', 'Reassigns in-flight with guardrails'],
        ['Customer ETAs / tracking', 'Typically out of scope', 'Native, telemetry-aware, branded'],
        ['Carrier orchestration', 'Cost-weighted assignment', 'Allocation plus scorecards and dispute artifacts'],
        ['Governance', 'Parameter and scenario control', 'Roles, approvals, audit logs, shadow mode'],
        ['Right move when', 'Bespoke optimization is a core advantage', 'Integration debt and manual reconciliation dominate'],
      ],
    },
    {
      type: 'h2',
      text: 'Hybrid architectures: solver plus execution layer',
    },
    {
      type: 'p',
      text: 'Large enterprises sometimes maintain a best-in-class optimizer while adopting an execution platform for live dispatch, telematics-informed ETAs, customer notifications, and exception workflows. This can work when integration contracts are explicit: which system owns driver assignment after a solve, how often plans refresh, and what happens when upstream data changes mid-shift. The failure mode is ambiguous ownership—two systems that can both mutate dispatch state without coordination.',
    },
    {
      type: 'p',
      text: 'If you pursue a hybrid, document event schemas and idempotency rules early. Operations teams pay the price when nightly batch solves conflict with intraday reality.',
    },
    {
      type: 'h2',
      text: 'When consolidation simplifies life',
    },
    {
      type: 'p',
      text: 'Consolidation makes sense when integration debt and manual reconciliation exceed the benefit of solver specialization. A unified platform can reduce vendor count, tighten security review, and give finance a clearer audit trail. The trade is flexibility: highly bespoke optimization research may still warrant specialist tools. The decision is economic and operational, not religious.',
    },
    {
      type: 'p',
      text: 'Ask whether your team actually uses the full depth of solver configuration weekly. Unused sophistication is complexity debt.',
    },
    {
      type: 'h2',
      text: 'Multi-hub and carrier orchestration',
    },
    {
      type: 'p',
      text: 'Enterprise networks rarely behave like single-depot experiments. Inventory positions, carrier scorecards, and regional labor rules interact. Dispatch platforms must represent those entities so planners do not export solves to spreadsheets to “fix” reality. Look for allocation policies that can incorporate historical reliability, not only cost.',
    },
    {
      type: 'p',
      text: 'Carrier orchestration also implies dispute readiness: timestamps, GPS correlation, and proof artifacts that stand up to audits when service credits are negotiated.',
    },
    {
      type: 'h2',
      text: 'Real-time visibility and customer-grade ETAs',
    },
    {
      type: 'p',
      text: 'Optimization produces intended arrival ordering; customers experience realized arrivals. Closing that gap requires traffic-aware modeling, driver status telemetry, and communication policies that smooth noisy updates. If your CX stack still shows static promises while dispatchers see live delays, you will fight escalations regardless of solve quality.',
    },
    {
      type: 'p',
      text: 'Evaluate whether proposed ETAs are computed from the same operational graph the dispatcher trusts—not a marketing estimate.',
    },
    {
      type: 'h2',
      text: 'Automation with governance',
    },
    {
      type: 'p',
      text: 'AI-assisted dispatch can recommend moves faster than humans, but enterprises require approvals, role separation, and logs. Especially across partners, automation without governance becomes a liability during incidents. Demand configurable guardrails: who can reassign high-value stops, when suggestions become actions, and how overrides are recorded.',
    },
    {
      type: 'p',
      text: 'Shadow mode—AI proposes, humans confirm—can build trust before full automation.',
    },
    {
      type: 'h2',
      text: 'Evaluation methodology for complex stacks',
    },
    {
      type: 'ol',
      items: [
        'Inventory master data readiness: geocodes, skills, vehicle capacities, and hub calendars.',
        'Latency requirements: seconds versus minutes between upstream changes and feasible new plans.',
        'Scenario library: peak week, hub outage, carrier shortfall, and extreme weather delay patterns.',
        'Financial traceability: cost per stop versus carrier invoices and payroll.',
        'Security posture: SSO, audit logs, and data residency if applicable.',
      ],
    },
    {
      type: 'p',
      text: 'Score vendors jointly with IT, operations, and finance to avoid selecting a technically elegant solver that finance cannot audit.',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Test recovery time, not optimality',
      text: 'Academic optimality is easy to demo and hard to feel in P&L. The metric that predicts enterprise success is recovery time—minutes from a disruption to a feasible, communicated plan. Put a hub outage and a carrier shortfall in your pilot and stopwatch it.',
    },
    {
      type: 'h2',
      text: 'Migration and organizational readiness',
    },
    {
      type: 'p',
      text: 'Enterprise migrations fail more often from process than technology. Name executive ownership, align incentive metrics across regions, and sequence training so dispatchers understand exceptions before advanced configuration. Celebrate early wins—fewer manual emails, faster recovery times—rather than waiting for a big-bang perfection milestone.',
    },
    {
      type: 'p',
      text: 'Keep a decision log when solver parameters change; future-you will need to explain service level shifts quarter to quarter.',
    },
    {
      type: 'h2',
      text: 'Where Geofleet fits for teams evaluating Locus-style ecosystems',
    },
    {
      type: 'p',
      text: 'Geofleet focuses on execution: AI-powered dispatch, routing under disruption, monitoring, and customer visibility with automation guardrails. For teams already invested in specialized optimization, Geofleet can serve as the operational layer that turns solves into reliable delivery outcomes. For teams seeking consolidation, Geofleet provides an integrated path that reduces integration surface area while keeping humans in control of high-stakes decisions.',
    },
    {
      type: 'p',
      text: 'Use the Locus comparison page for positioning, then pressure-test any short list with the scenarios in this guide—not slide decks.',
    },
    {
      type: 'ctaRow',
      items: [ctaAgents, ctaDemo],
    },
    {
      type: 'relatedReading',
      heading: 'Comparison pages',
      links: [
        commonLinks.locus,
        commonLinks.routeOpt,
        commonLinks.onfleet,
        commonLinks.bringg,
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaTrial, ctaAgents],
    },
  ],
  faq: [
    {
      question: 'Should we rip out our existing optimizer on day one?',
      answer:
        'Rarely. Many enterprises run phased adoption: execution and visibility first, then deeper automation, and solver changes only when data readiness and ownership are clear.',
    },
    {
      question: 'What is the most underrated integration for enterprise routing?',
      answer:
        'Inventory and order validity signals. Solving against stale inventory produces expensive fiction.',
    },
    {
      question: 'How do we compare vendors with different AI claims?',
      answer:
        'Focus on measurable outcomes under your scenarios: recovery time, exception dwell, CX alignment, and manual minutes—not model names.',
    },
    {
      question: 'What should network planners watch during a pilot?',
      answer:
        'Variance across regions. Averages hide weak hubs that will become tomorrow’s bottleneck.',
    },
    {
      question: 'Is on-prem required for enterprise security?',
      answer:
        'Depends on industry and region. Many teams use cloud with strong DPAs, encryption, and SSO. Align InfoSec early rather than after vendor selection.',
    },
    {
      question: 'How do we handle overlapping territories?',
      answer:
        'Define ownership rules and escalation paths before optimizing. Geometry-first solves struggle when commercial rules conflict with distance minima.',
    },
    {
      question: 'Can Geofleet coexist with specialized optimization research tools?',
      answer:
        'Yes, when integration contracts and ownership of dispatch state are explicit. The platform is designed for execution-grade operations whether routing is native or fed externally.',
    },
    {
      question: 'What is a realistic timeline to value?',
      answer:
        'Teams often see measurable manual reduction within one peak cycle if pilots are scoped honestly and baselines are captured up front.',
    },
  ],
};

/**
 * Shared "[Competitor] vs Geofleet at a glance" table used by competitor-comparison posts.
 * Geofleet facts mirror the public pricing page (src/i18n/local/en/pricing.ts); keep them in sync.
 */
export interface CompetitorGlance {
  bestFor: string;
  coreFocus: string;
  routeOptimization: string;
  dispatch: string;
  multiHub: string;
  tracking: string;
  integrations: string;
  pricing: string;
  freePlan: string;
}

export const geofleetGlance: CompetitorGlance = {
  bestFor: 'Growing delivery teams that need dispatch, routing and tracking in one platform, from 10 drivers to multi-hub fleets',
  coreFocus: 'Delivery execution: dispatch, route optimization, live tracking and AI-assisted operations',
  routeOptimization: 'Included from the Professional plan',
  dispatch: 'Dispatch console on every plan (2 dispatchers on Professional, unlimited on Enterprise); AI auto-dispatch and auto-allocation on Premium ($29/driver/month); full AI agent suite on Enterprise',
  multiHub: 'Multi-hub and hybrid owned-fleet/carrier operations with the multi-location dashboard on Enterprise',
  tracking: 'Live tracking, customer ETAs and photo POD on every plan; SMS/WhatsApp from Professional; branded tracking pages, barcode scanning and ID verification on Premium',
  integrations: 'API and webhooks from Professional; e-commerce, POS and ERP integrations',
  pricing: 'From $49/month on Lite (10 drivers and 2,000 orders, one hub, contact us for additional capacity); full platform per driver with defined monthly order allowances: Professional $19 and Premium $29 per driver/month (minimum 10 drivers, billed quarterly); Enterprise custom',
  freePlan: 'No free plan. Lite: $49/month for 3 drivers and 2,000 orders from one hub (contact us for additional capacity); 14-day Professional trial',
};

export function competitorGlanceTable(
  competitor: string,
  c: CompetitorGlance,
  caption?: string,
): { type: 'table'; caption?: string; headers: string[]; rows: string[][] } {
  const g = geofleetGlance;
  return {
    type: 'table',
    caption: caption ?? `Geofleet vs ${competitor}: key differences (September 2026)`,
    headers: ['', 'Geofleet', competitor],
    rows: [
      ['Best for', g.bestFor, c.bestFor],
      ['Core focus', g.coreFocus, c.coreFocus],
      ['Route optimization', g.routeOptimization, c.routeOptimization],
      ['Dispatch & allocation', g.dispatch, c.dispatch],
      ['Multi-hub / carriers', g.multiHub, c.multiHub],
      ['Customer tracking & POD', g.tracking, c.tracking],
      ['Integrations / API', g.integrations, c.integrations],
      ['Pricing model', g.pricing, c.pricing],
      ['Free plan', g.freePlan, c.freePlan],
    ],
  };
}
